import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { CheckCircle2, Loader2, Send } from 'lucide-react';
import { functions } from './firebase.config';

// "Request early access" form on the public landing page (2026-10-10).
// Submits to the pre-auth adminDashboardF-submitEarlyAccessRequest callable;
// the owner gets an email and the request appears on the portal Support tab.
// No account, no cookies, nothing stored client-side.

const CONTACT_EMAIL = 'info@simplicitydynamics.ca';
const CREW_SIZES = ['1-5', '6-15', '16-50', '51+'];
const TRADES = [
  'Flooring',
  'Drywall',
  'Painting',
  'Electrical',
  'Plumbing',
  'HVAC',
  'Concrete',
  'Framing',
  'Roofing',
  'Landscaping',
  'General contractor',
];

const EMPTY = { companyName: '', contactName: '', email: '', phone: '', trade: '', crewSize: '', message: '', website: '' };

const inputClass =
  'mt-1 w-full rounded-lg border border-[#96a5b4]/50 bg-white px-3 py-2 text-[15px] text-[#1d2b33] outline-none transition-colors placeholder:text-[#96a5b4] focus:border-[#2f66b8] focus:ring-2 focus:ring-[#2f66b8]/20';

const Field = ({ label, children, optional }) => (
  <label className="block text-sm font-medium text-[#3c4f5e]">
    {label}
    {optional && <span className="ml-1 font-normal text-[#96a5b4]">(optional)</span>}
    {children}
  </label>
);

const EarlyAccessForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const submittingRef = useRef(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError('');
    try {
      const submit = httpsCallable(functions, 'adminDashboardF-submitEarlyAccessRequest');
      await submit(form);
      setDone(true);
    } catch (err) {
      // Only validation messages come from the server in plain words; any
      // other failure (network, quota) gets one generic line.
      setError(err?.code === 'functions/invalid-argument' ? err.message : 'We could not send your request.');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <CheckCircle2 size={40} className="text-[#2ea183]" />
        <h3 className="text-xl font-semibold">Thanks, {form.contactName.trim() || 'we got it'}.</h3>
        <p className="max-w-sm text-[#64798a]">
          We&rsquo;ll be in touch at <span className="font-medium text-[#1d2b33]">{form.email.trim()}</span>{' '}
          about early access for {form.companyName.trim()}.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Company">
          <input className={inputClass} value={form.companyName} onChange={set('companyName')} required maxLength={120} autoComplete="organization" />
        </Field>
        <Field label="Your name">
          <input className={inputClass} value={form.contactName} onChange={set('contactName')} required maxLength={80} autoComplete="name" />
        </Field>
        <Field label="Email">
          <input className={inputClass} type="email" value={form.email} onChange={set('email')} required maxLength={254} autoComplete="email" />
        </Field>
        <Field label="Phone" optional>
          <input className={inputClass} type="tel" value={form.phone} onChange={set('phone')} maxLength={40} autoComplete="tel" />
        </Field>
        <Field label="Trade" optional>
          <input className={inputClass} list="early-access-trades" value={form.trade} onChange={set('trade')} maxLength={60} placeholder="Flooring, drywall, electrical..." />
          <datalist id="early-access-trades">
            {TRADES.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </Field>
        <Field label="Crew size" optional>
          <select className={inputClass} value={form.crewSize} onChange={set('crewSize')}>
            <option value="">Pick one</option>
            {CREW_SIZES.map((s) => (
              <option key={s} value={s}>
                {s} people
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Anything else" optional>
        <textarea className={inputClass} rows={3} value={form.message} onChange={set('message')} maxLength={1000} placeholder="What would help you most?" />
      </Field>
      {/* Honeypot: hidden from people, filled by bots. The server ignores
          submissions that carry a value here. */}
      <div className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden" aria-hidden="true">
        <label>
          Website
          <input tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} />
        </label>
      </div>

      {error && (
        <p className="rounded-lg border border-[#d96a5b]/40 bg-[#d96a5b]/10 px-3 py-2 text-sm text-[#a8402f]">
          {error} Please write to{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium underline">
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#2f66b8] px-6 py-3 font-semibold text-white transition-colors hover:bg-[#2a5ba6] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
      >
        {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        {submitting ? 'Sending...' : 'Request early access'}
      </button>
      <p className="text-xs text-[#64798a]">
        We use this only to get back to you about early access. See our{' '}
        <Link to="/privacy" className="font-medium text-[#2f66b8] hover:underline">
          Privacy Policy
        </Link>
        . This site is protected by reCAPTCHA and the Google{' '}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">
          Privacy Policy
        </a>{' '}
        and{' '}
        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">
          Terms of Service
        </a>{' '}
        apply.
      </p>
    </form>
  );
};

export default EarlyAccessForm;
